// 🔹 Stylesheets
import './ForgotPassword.scss';

// 🔹 React Core
import React, { useState, useEffect } from 'react';

// 🔹 Routing
import { useNavigate } from 'react-router-dom';

// 🔹 Components
import { Button } from '../../components/Button/Button';

// 🔹 Assets
import { useAuthResetPassword } from 'src/api/mutations/useAuthResetPassword';
import { toast } from 'src/utils/appAlert';
import { ReactComponent as InfoIcon } from '../../../../assets/images/onboarding-trainer/info.svg';
import { ReactComponent as EmailIcon } from '../../../../assets/images/onboarding-trainer/email.svg';
import { ReactComponent as BackIcon } from '../../../../assets/images/onboarding-trainer/back.svg';
import { ReactComponent as LogoIcon } from '../../../../assets/images/onboarding-trainer/logo.svg';

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const { mutateAsync: resetPassword, isPending } = useAuthResetPassword();

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (cooldown > 0) {
      timer = setTimeout(() => setCooldown((prev) => prev - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResetPassword = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setErrors({ email: 'Please enter your email address' });
      return;
    }

    try {
      await resetPassword({ email });

      setErrors({});
      if (submitted) {
        toast.success('Reset link resent successfully!');
      }
      setSubmitted(true);
      setCooldown(60); // Start 60-second cooldown
    } catch (error: any) {
      let errorMessage = 'Failed to send reset link.';
      if (error.code === 'auth/user-not-found' || error.message === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.message) {
        errorMessage = error.message
          .replace('Firebase: Error ', '')
          .replace(/\(auth.*\)\./, '')
          .trim();
      }
      setErrors({ email: errorMessage });
    }
  };

  return (
    <div className="onboarding-forgot-password-container">
      <div className="forgot-password-box">
        <div className="forgot-password-header">
          <div className="logo-placeholder">{<LogoIcon />}</div>
          <h1 className="forgot-password-title">
            {submitted ? 'Check your email' : 'Forgot password?'}
          </h1>
          <p className="forgot-password-subtitle">
            {submitted
              ? "If an account exists for this email, you'll receive a password reset link shortly."
              : 'Enter your email and we’ll send you reset instructions.'}
          </p>
        </div>

        {!submitted ? (
          <form
            onSubmit={handleResetPassword}
            className="forgot-password-form auth-form-common"
            noValidate
          >
            <div className="input-group">
              <label>EMAIL</label>
              <input
                type="email"
                placeholder="john.doe@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors({ ...errors, email: '' });
                }}
                className={errors.email ? 'error-input' : ''}
              />
              {errors.email && (
                <div className="error-message">
                  {<InfoIcon />} <span>{errors.email}</span>
                </div>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              className="submit-btn bg-white"
              disabled={isPending}
            >
              {isPending ? 'Sending...' : 'Send reset link'}
            </Button>
          </form>
        ) : (
          <div className="email-sent-card-container">
            <div className="email-sent-info-box">
              <div className="email-icon-wrapper">{<EmailIcon />}</div>
              <div className="email-details">
                <span className="email-sent-label">EMAIL SENT TO:</span>
                <span className="email-address">{email}</span>
              </div>
            </div>

            <Button
              variant="primary"
              className="submit-btn bg-white w-100"
              onClick={() => {
                window.location.href = `mailto:${email}`;
              }}
            >
              Open email app
            </Button>

            <div className="resend-link-text">
              Didn&apos;t receive the email?{' '}
              <button
                className={`resend-link-btn ${cooldown > 0 ? 'disabled-link' : ''}`}
                onClick={handleResetPassword}
                disabled={isPending || cooldown > 0}
                style={{
                  opacity: cooldown > 0 ? 0.6 : 1,
                  cursor: cooldown > 0 ? 'not-allowed' : 'pointer',
                }}
              >
                {isPending
                  ? 'Sending...'
                  : cooldown > 0
                    ? `Resend link in ${cooldown}s`
                    : 'Resend link'}
              </button>
            </div>
          </div>
        )}

        <Button
          variant="outline"
          className="submit-btn m-auto back-to-login"
          onClick={() => navigate('/onboarding-trainer/login')}
        >
          {<BackIcon />} Back to Log in
        </Button>
      </div>
      <div className="forgot-password-bg-glow"></div>
    </div>
  );
};
